import React, { useEffect } from "react";
import { Card, Container } from "reactstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import HeaderCreateItem from "components/Common/HeaderCreateItem";
import { CommonButton } from "components/Common/ButtonCommon";
import useGetFormSchema from "custom-hook/useGetFormSchema";
import Table from "components/form-control/Table";
import { CommonText } from "components/Common/TextCommon";
import VVSSelectRelation from "components/Common/VVSSelectRelation";
import { addVoucher, updateVoucher } from "redux-toolkit/slices/CMS/Voucher";
import httpService from "services/httpService";
import { toastrErrorAlert } from "components/Common/AlertToastr";
import moment from "moment";
import { formatNumber } from "helpers/erp_helper";

const HEADERS = [
  {
    text: "Brands",
    CellComponent: VVSSelectRelation,
    cellComponentProps: (formValue, indexOfRow) => ({
      name: `policy.${indexOfRow}.brands`,
      model: "Brand",
    }),
  },
  {
    text: "Categories",
    CellComponent: VVSSelectRelation,
    cellComponentProps: (formValue, indexOfRow) => ({
      name: `policy.${indexOfRow}.categories`,
      model: "Category",
    }),
  },
  {
    text: "Products",
    CellComponent: VVSSelectRelation,
    cellComponentProps: (formValue, indexOfRow) => ({
      name: `policy.${indexOfRow}.products`,
      model: "Product",
    }),
  },
];

const NewVoucher = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    getVoucherDetail();
  }, [id]);

  const schema = yup
    .object({
      name: yup.string().required("This field is required!"),
      budget: yup.lazy(value =>
        typeof value === "string"
          ? yup
              .string()
              .transform((_, val) => val && Number(val.replaceAll(".", "")))
          : yup
              .number()
              .typeError(`Số lượng phải là số!`)
              .nullable()
              .moreThan(0, "Floor area cannot be negative")
      ),
      campaign: yup.lazy(value =>
        typeof value === "object"
          ? yup
              .object()
              .nullable()
              .transform((_, val) =>
                val?.text
                  ? {
                      objectId: val.objectId,
                      __type: "Pointer",
                      className: "Campaign",
                    }
                  : null
              )
          : yup.string()
      ),
      startDate: yup
        .object()
        .shape({ __type: yup.string(), iso: yup.string() })
        .nullable()
        .transform((_, val) => (val ? { __type: "Date", iso: val } : null)),
      endDate: yup
        .object()
        .shape({ __type: yup.string(), iso: yup.string() })
        .nullable()
        .transform((_, val) => (val ? { __type: "Date", iso: val } : null)),
    })
    .required();

  const {
    handleSubmit,
    formState: { errors },
    ...rest
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      budget: 1,
      startDate: moment().format("YYYY-MM-DD"),
      endDate: moment().add(1, "months").format("YYYY-MM-DD"),
    },
    resolver: yupResolver(schema),
  });

  const { renderForm } = useGetFormSchema("Voucher", yup, rest, errors);

  const getVoucherDetail = async () => {
    if (!id) return;

    try {
      const res = await httpService.get(
        `/parse/classes/Voucher/${id}?include=["campaign"]`
      );

      const { reset } = rest;
      reset({
        ...res,
        startDate: moment(res?.startDate?.iso || "").format("YYYY-MM-DD"),
        endDate: moment(res?.endDate?.iso || "").format("YYYY-MM-DD"),
        budget: res?.budget ? formatNumber(res.budget) : "",
        campaign: res?.campaign
          ? {
              text: res.campaign?.name,
              objectId: res.campaign.objectId,
              className: "Campaign",
              __type: "Pointer",
            }
          : { text: "" },
      });
    } catch (errorRes) {
      if (errorRes?.response?.data?.code === 101) {
        toastrErrorAlert("Không tìm thấy Voucher!");
        history.replace("/voucher");
      }
    }
  };

  const onSubmit = async values => {
    const { createdAt, updatedAt, ...voucherValues } = values;

    const voucher = {
      ...voucherValues,
      displayApp: voucherValues.displayApp ? "Yes" : "No",
    };

    if (id) {
      dispatch(updateVoucher({ dataItem: voucher, dataId: id }));
      return;
    }

    const res = await dispatch(addVoucher(voucher));
    history.replace(`/voucher/${res.payload.objectId}`);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <HeaderCreateItem title="Add / Edit Voucher">
            <CommonButton level={0} type="submit">
              Save
            </CommonButton>
          </HeaderCreateItem>
          {renderForm()}
          <Card body>
            <CommonText level={1} className="m-0">
              Conditions
            </CommonText>
            <Table headers={HEADERS} formProps={{ ...rest }} name="policy" />
          </Card>
        </form>
      </Container>
    </div>
  );
};

export default NewVoucher;
